import { useState, useEffect, useCallback, useRef } from 'react'
import { sourceDocumentStatusManager, SourceDocumentStatus } from '../services/SourceDocumentStatusManager'

/**
 * Hook for managing source document statuses globally
 * Provides persistent source document status management that survives component unmounts
 */
export const useSourceDocumentStatus = () => {
  const [statuses, setStatuses] = useState({})
  const [stats, setStats] = useState({})
  const listenerRef = useRef(null)

  // Update local state when global state changes
  const updateState = useCallback(() => {
    setStatuses(sourceDocumentStatusManager.getAllStatuses())
    setStats(sourceDocumentStatusManager.getStats())
  }, [])

  // Set up event listener
  useEffect(() => {
    updateState()
    
    listenerRef.current = sourceDocumentStatusManager.addEventListener((event) => {
      console.log('Document status event:', event.type, event.data)
      updateState()
    })
    
    // Cleanup
    return () => {
      if (listenerRef.current) {
        listenerRef.current()
      }
    }
  }, [updateState])

  // Document status management functions
  const getStatus = useCallback((documentId) => {
    return sourceDocumentStatusManager.getStatus(documentId)
  }, [])

  const setStatus = useCallback((documentId, status) => {
    sourceDocumentStatusManager.setStatus(documentId, status)
  }, [])

  const startIngestion = useCallback(async (config) => {
    return await sourceDocumentStatusManager.startIngestion(config)
  }, [])

  const cancelPolling = useCallback((documentId) => {
    sourceDocumentStatusManager.cancelPolling(documentId)
  }, [])

  const cancelAllPolling = useCallback(() => {
    return sourceDocumentStatusManager.cancelAllPolling()
  }, [])

  const resetStatus = useCallback((documentId) => {
    sourceDocumentStatusManager.resetStatus(documentId)
  }, [])

  const isPolling = useCallback((documentId) => {
    return sourceDocumentStatusManager.isPolling(documentId)
  }, [])

  const getActivePollingDocuments = useCallback(() => {
    return sourceDocumentStatusManager.getActivePollingDocuments()
  }, [])

  const getPollInfo = useCallback((documentId) => {
    return sourceDocumentStatusManager.getPollInfo(documentId)
  }, [])

  const getSourceDocumentsByStatus = useCallback((status) => {
    return sourceDocumentStatusManager.getSourceDocumentsByStatus(status)
  }, [])

  const clearAll = useCallback(() => {
    sourceDocumentStatusManager.clearAll()
  }, [])

  const forceRefresh = useCallback(() => {
    updateState()
  }, [updateState])

  const forceStopPollingByTaskId = useCallback((taskId) => {
    return sourceDocumentStatusManager.forceStopPollingByTaskId(taskId)
  }, [])

  const getPollingDebugInfo = useCallback(() => {
    return sourceDocumentStatusManager.getPollingDebugInfo()
  }, [])

  // File path relationship methods
  const setDocumentFilePath = useCallback((documentId, filePath, connectorId) => {
    sourceDocumentStatusManager.setDocumentFilePath(documentId, filePath, connectorId)
  }, [])

  const getDocumentFilePath = useCallback((documentId) => {
    return sourceDocumentStatusManager.getDocumentFilePath(documentId)
  }, [])

  const removeDocumentFilePath = useCallback((documentId) => {
    return sourceDocumentStatusManager.removeDocumentFilePath(documentId)
  }, [])

  const findDocumentByFilePath = useCallback((filePath) => {
    return sourceDocumentStatusManager.findDocumentByFilePath(filePath)
  }, [])

  return {
    // State
    statuses,
    stats,
    
    // Status management
    getStatus,
    setStatus,
    resetStatus,
    
    // File path relationships
    setDocumentFilePath,
    getDocumentFilePath,
    removeDocumentFilePath,
    findDocumentByFilePath,
    
    // Ingestion
    startIngestion,
    cancelPolling,
    cancelAllPolling,
    isPolling,
    
    // Queries
    getActivePollingDocuments,
    getPollInfo,
    getSourceDocumentsByStatus,
    
    // Utilities
    clearAll,
    forceRefresh,
    
    // Debugging and force operations
    forceStopPollingByTaskId,
    getPollingDebugInfo,
    
    // Constants
    SourceDocumentStatus
  }
}

/**
 * Hook for tracking a specific document's status
 */
export const useSourceDocumentStatusById = (documentId) => {
  const [status, setStatus] = useState(SourceDocumentStatus.WAITING)
  const [isPolling, setIsPolling] = useState(false)
  const [pollInfo, setPollInfo] = useState(null)
  const listenerRef = useRef(null)

  const updateStatus = useCallback(() => {
    if (documentId) {
      setStatus(sourceDocumentStatusManager.getStatus(documentId))
      setIsPolling(sourceDocumentStatusManager.isPolling(documentId))
      setPollInfo(sourceDocumentStatusManager.getPollInfo(documentId))
    }
  }, [documentId])

  useEffect(() => {
    updateStatus()
    
    listenerRef.current = sourceDocumentStatusManager.addEventListener((event) => {
      // Only update if this event affects our document
      if (event.data && event.data.documentId === documentId) {
        updateStatus()
      }
    })
    
    return () => {
      if (listenerRef.current) {
        listenerRef.current()
      }
    }
  }, [documentId, updateStatus])

  const setSourceDocumentStatus = useCallback((newStatus) => {
    if (documentId) {
      sourceDocumentStatusManager.setStatus(documentId, newStatus)
    }
  }, [documentId])

  const startDocumentIngestion = useCallback(async (config) => {
    if (documentId) {
      return await sourceDocumentStatusManager.startIngestion({
        ...config,
        documentId
      })
    }
  }, [documentId])

  const cancelDocumentPolling = useCallback(() => {
    if (documentId) {
      sourceDocumentStatusManager.cancelPolling(documentId)
    }
  }, [documentId])

  const resetSourceDocumentStatus = useCallback(() => {
    if (documentId) {
      sourceDocumentStatusManager.resetStatus(documentId)
    }
  }, [documentId])

  const setDocumentFilePath = useCallback((filePath, connectorId) => {
    if (documentId) {
      sourceDocumentStatusManager.setDocumentFilePath(documentId, filePath, connectorId)
    }
  }, [documentId])

  const getDocumentFilePath = useCallback(() => {
    if (documentId) {
      return sourceDocumentStatusManager.getDocumentFilePath(documentId)
    }
    return null
  }, [documentId])

  const removeDocumentFilePath = useCallback(() => {
    if (documentId) {
      return sourceDocumentStatusManager.removeDocumentFilePath(documentId)
    }
    return null
  }, [documentId])

  return {
    // State
    status,
    isPolling,
    pollInfo,
    documentId,
    
    // Actions
    setStatus: setSourceDocumentStatus,
    startIngestion: startDocumentIngestion,
    cancelPolling: cancelDocumentPolling,
    resetStatus: resetSourceDocumentStatus,
    
    // File path relationships
    setDocumentFilePath,
    getDocumentFilePath,
    removeDocumentFilePath,
    
    // Computed properties
    isWaiting: status === SourceDocumentStatus.WAITING,
    isUploading: status === SourceDocumentStatus.UPLOADING,
    isReview: status === SourceDocumentStatus.REVIEW,
    isActive: status === SourceDocumentStatus.UPLOADING,
    
    // Constants
    SourceDocumentStatus
  }
}